import { Link } from "react-router-dom";
import { Egg, PawPrint, CalendarDays, Users, MapPin, BadgeCheck, Check, X, UserPlus } from "lucide-react";
import { photoUrl } from "../../lib/api";
import { dueIn } from "../../lib/litters";

export const SPECIES_FR = { Dog: "Chien", Cat: "Chat", Bird: "Oiseau", Rabbit: "Lapin", Reptile: "Reptile", "Small animal": "Petit animal", Livestock: "Bétail", Other: "Autre" };

const fmtDate = (iso, lang) => {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00`);
  return isNaN(d) ? "" : d.toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { month: "short", day: "numeric", year: "numeric" });
};

const WaitlistButton = ({ l, fr, onJoin, onLeave }) => {
  const stop = (fn) => (e) => { e.preventDefault(); e.stopPropagation(); fn(l); };
  if (l.my_entry) {
    return (
      <button onClick={stop(onLeave)} data-testid={`litter-card-leave-${l.id}`}
        className="group/w w-full h-10 rounded-xl border-2 border-emerald-500 bg-emerald-50 text-emerald-700 text-sm font-semibold inline-flex items-center justify-center gap-1.5 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-600 transition-colors">
        <Check size={15} className="group-hover/w:hidden" /><X size={15} className="hidden group-hover/w:block" />
        <span className="group-hover/w:hidden">{fr ? `Sur la liste · nº ${l.my_entry.position}` : `On waitlist · #${l.my_entry.position}`}</span>
        <span className="hidden group-hover/w:inline">{fr ? "Quitter la liste" : "Leave waitlist"}</span>
      </button>
    );
  }
  const blocked = !l.waitlist_open ? (fr ? "Liste fermée" : "Waitlist closed") : l.is_full ? (fr ? "Liste complète" : "Waitlist full") : "";
  return (
    <button onClick={stop(onJoin)} disabled={!!blocked} data-testid={`litter-card-join-${l.id}`}
      className="w-full h-10 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors disabled:bg-slate-200 disabled:text-slate-500">
      {!blocked && <UserPlus size={15} />} {blocked || (fr ? "Rejoindre la liste" : "Join waitlist")}
    </button>
  );
};

export const LitterCard = ({ l, lang, mine, onJoin, onLeave }) => {
  const fr = lang === "fr";
  const clutch = l.kind === "clutch";
  const arrived = l.arrived ?? l.status === "arrived";
  const badge = arrived ? (fr ? "Arrivée" : "Arrived") : (fr ? "À venir" : "Upcoming");
  const when = fmtDate(l.expected_date, lang);
  return (
    <Link to={`/seller/${l.breeder.user_id}#litters`} data-testid={`litter-card-${l.id}`}
      className="group bg-white rounded-2xl border border-slate-200/80 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200 flex flex-col">
      <div className="relative h-40 bg-gradient-to-br from-[#FFF4F0] to-[#EEF4FF] overflow-hidden">
        {l.photo
          ? <img src={photoUrl(l.photo)} alt={l.breed} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
          : <div className="h-full w-full flex items-center justify-center text-[#F5551E]/40">{clutch ? <Egg size={44} /> : <PawPrint size={44} />}</div>}
        <span className={`absolute top-3 left-3 text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${arrived ? "bg-emerald-600 text-white" : "bg-[#12234A] text-white"}`}>{badge}</span>
        {l.is_full && <span className="absolute top-3 right-3 text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/90 text-slate-600">{fr ? "Liste complète" : "Waitlist full"}</span>}
        {dueIn(l, lang) && <span className="absolute bottom-3 left-3 text-xs font-bold px-3 py-1.5 rounded-full bg-[#F5551E] text-white shadow-md inline-flex items-center gap-1.5" data-testid={`litter-countdown-${l.id}`}><CalendarDays size={13} /> {dueIn(l, lang)}</span>}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <p className="text-[11px] font-bold uppercase tracking-wider text-[#F5551E]">{fr ? SPECIES_FR[l.species] || l.species : l.species}</p>
        <h3 className="font-display font-bold text-[#12234A] leading-snug mt-0.5 line-clamp-2">{l.title || `${l.breed} ${fr ? (clutch ? "couvée" : "portée") : l.kind}`}</h3>
        <div className="mt-2 space-y-1 text-xs text-slate-500">
          {when && <p className="flex items-center gap-1.5"><CalendarDays size={13} /> {arrived ? (fr ? "Le " : "On ") : (fr ? "Prévu le " : "Due ")}{when}</p>}
          <p className="flex items-center gap-1.5"><Users size={13} /> {l.waitlist_count} {fr ? "en attente" : "on waitlist"}{l.expected_count ? ` · ${l.expected_count} ${fr ? "prévus" : "expected"}` : ""}</p>
          {l.price_note && <p className="font-semibold text-[#12234A]">{l.price_note}</p>}
        </div>
        <div className="mt-auto pt-3 border-t border-slate-100 text-xs">
          <p className="flex items-center gap-1.5 font-semibold text-[#12234A] leading-snug">
            <span className="line-clamp-2">{l.breeder.name}</span>
            {l.breeder.verified_breeder && <BadgeCheck size={14} className="text-purple-600 shrink-0" />}
          </p>
          {l.breeder.location && <p className="flex items-center gap-1 text-slate-400 mt-1 truncate"><MapPin size={12} className="shrink-0" /> {l.breeder.location}</p>}
        </div>
        {onJoin && !mine && <div className="mt-3"><WaitlistButton l={l} fr={fr} onJoin={onJoin} onLeave={onLeave} /></div>}
      </div>
    </Link>
  );
};
